import { z } from 'zod';
import { apiEnvelope } from '../envelope';

/**
 * Which identifier space a member reference resolved in.
 *
 * Both a Galapagos `members.id` and a backend2 `legacy_backend2_sub` are opaque
 * UUIDs, so a client holding one cannot tell which it is. The answer decides
 * which estate's access gate governs the view, which is why it is the one fact
 * about the member this contract returns.
 */
export const MEMBER_ACCESS_ID_SPACE = ['member_id', 'legacy_backend2_sub'] as const;
export const memberAccessIdSpaceSchema = z.enum(MEMBER_ACCESS_ID_SPACE);
export type MemberAccessIdSpace = z.infer<typeof memberAccessIdSpaceSchema>;

const grantedAssignedShape = z.object({
  access: z.literal('granted'),
  basis: z.literal('assigned'),
  idSpace: memberAccessIdSpaceSchema,
});

const grantedJustificationSchema = z.object({
  access: z.literal('granted'),
  basis: z.literal('recorded_justification'),
  /** ISO-8601 UTC instant the live justification grant runs until. */
  grantedUntil: z.string().min(1),
  idSpace: memberAccessIdSpaceSchema,
});

const justificationRequiredShape = z.object({
  access: z.literal('justification_required'),
  idSpace: memberAccessIdSpaceSchema,
});

const notFoundShape = z.object({
  access: z.literal('not_found'),
});

/**
 * The answer's shape as documented: the four variants and nothing more.
 *
 * The OpenAPI generator cannot render a never-present field, so the published
 * document describes this, while runtime parsing uses the guarded union below,
 * built from these same objects so the two cannot drift.
 */
export const memberAccessDecisionDocumentedDataSchema = z.union([
  grantedAssignedShape,
  grantedJustificationSchema,
  justificationRequiredShape,
  notFoundShape,
]);
export const memberAccessDecisionDocumentedResponseSchema = apiEnvelope(
  memberAccessDecisionDocumentedDataSchema,
);

// A variant that must not carry an expiry declares it as never-present rather
// than going `.strict()`: a strict object would also reject keys a later server
// adds (ADR-135 Rule 1), while this rejects only the one combination that would
// let a caller read a window off an answer that grants none.
const noExpiry = { grantedUntil: z.never().optional() };

/**
 * Whether the caller may open a member, and nothing else about the member.
 *
 * The answer deliberately carries no member data — no name, no contact field,
 * no care-team member, and not the other identifier of the id/sub pair — so it
 * can be asked BEFORE any justification exists without delivering anything the
 * justification is meant to precede.
 *
 * A plain union rather than a discriminated one: both `granted` variants share
 * the `access` value and differ on `basis`. Narrowing on `access`, then on
 * `basis`, is what makes `grantedUntil` reachable only where it exists.
 */
export const memberAccessDecisionDataSchema = z.union([
  grantedAssignedShape.extend(noExpiry),
  grantedJustificationSchema,
  justificationRequiredShape.extend(noExpiry),
  // A reference that names no member. Answered as itself so a client can tell a
  // member that lives only in Pelago 1 from one Galapagos knows, and choose the
  // justification dialog by where the member lives. It carries no id space:
  // there is no member to have matched in one.
  notFoundShape.extend({ ...noExpiry, idSpace: z.never().optional() }),
]);
export type MemberAccessDecisionData = z.infer<typeof memberAccessDecisionDataSchema>;

export const memberAccessDecisionResponseSchema = apiEnvelope(memberAccessDecisionDataSchema);
export type MemberAccessDecisionResponse = z.infer<typeof memberAccessDecisionResponseSchema>;
